import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { useRouter } from 'next/router'
import { ReactNode } from 'react'
import {
  GlobalCatcher,
  NavigationResult,
  Settings,
} from '../../@types/statamic'
import useWindowScrolled from '../../hooks/useWindowScrolled'
import Navigation from '../../lib/Navigation'
import Footer from './Footer'
import TopDesktopNavigation from './TopDesktopNavigation'
import TopMobileNavigation from './TopMobileNavigation'
import Catcher from '@/src/components/elements/Catcher'
import Container from '@/src/components/layout/Container'
import { statamicConfig } from '@/src/StatamicGraphQLClient'

export default function BaseLayout({
  children,
  navs,
  settings,
  globalCatcher,
  hideNavigation = false,
}: {
  children: ReactNode
  navs: NavigationResult['navs']
  settings: Settings['settings']
  globalCatcher: GlobalCatcher['globalCatcher']
  hideNavigation?: boolean
}) {
  const router = useRouter()

  const scrolled = useWindowScrolled(10)

  // prevent transitioning on change of query params
  const transitionKey = router.asPath.split('?')[0]

  const navigations = Navigation(navs)

  const primaryNavigation = navigations.get(statamicConfig.navHandle)
  const secondaryNavigation = navigations.get(
    statamicConfig.secondaryNavHandle!
  )

  return (
    <div className="relative flex min-h-screen flex-col">
      <div
        className={clsx('fixed inset-x-0 top-0 z-10', {
          'bg-white shadow-lg': scrolled,
        })}
        style={{
          paddingRight: 'var(--removed-body-scroll-bar-size, 0px)',
        }}
      >
        {hideNavigation ? (
          <Container>
            <div className="flex h-16 items-center xl:h-24">
              {settings.logo && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={settings.logo.permalink}
                  alt={settings.logo.alt}
                  className={clsx({
                    'w-24 xl:w-32':
                      process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE === 'blink',
                    'w-[92px]':
                      process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE === 'nohe',
                  })}
                />
              )}
            </div>
          </Container>
        ) : (
          <>
            <div className="hidden xl:block">
              <TopDesktopNavigation
                logo={settings.logo}
                scrolled={scrolled}
                mainNavigation={primaryNavigation}
                secondaryNavigation={secondaryNavigation}
              />
            </div>
            <div className="xl:hidden">
              <TopMobileNavigation
                logo={settings.logo}
                scrolled={scrolled}
                mainNavigation={primaryNavigation}
                secondaryNavigation={secondaryNavigation}
              />
            </div>
            <Catcher {...globalCatcher} />
          </>
        )}
      </div>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          className="relative z-0 flex-1"
          key={transitionKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
      {!hideNavigation && (
        <footer>
          <Footer navs={navs} settings={settings} />
        </footer>
      )}
    </div>
  )
}
